import { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, ExternalLink, AlertCircle, RefreshCw, ChevronDown, Share2, Check as CheckIcon } from 'lucide-react'
import { SEO } from '@/components/seo'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'

const API_URL = import.meta.env.VITE_API_URL || '/api/v1'

interface DetectorResult {
  slug: string
  name: string
  url: string
  score: number | null
  label: string | null
  error?: string
  ms: number
}

interface CheckResponse {
  success: boolean
  id?: string
  imageUrl?: string
  results: DetectorResult[]
  summary: { ai: number; human: number; uncertain: number; error: number }
  verdict: 'AI' | 'HUMAN' | 'INCONCLUSIVE'
  remaining?: number
}

function getLabelColor(label: string | null) {
  if (label === 'AI') return 'bg-red-100 text-red-700 border-red-200'
  if (label === 'HUMAN') return 'bg-green-100 text-green-700 border-green-200'
  if (label === 'UNCERTAIN') return 'bg-yellow-100 text-yellow-700 border-yellow-200'
  return 'bg-gray-100 text-gray-500 border-gray-200'
}

function getScoreBarColor(score: number) {
  if (score >= 0.7) return 'bg-red-500'
  if (score >= 0.3) return 'bg-yellow-500'
  return 'bg-green-500'
}

function getVerdictStyle(verdict: string) {
  if (verdict === 'AI') return { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Likely AI-Generated' }
  if (verdict === 'HUMAN') return { bg: 'bg-green-50 border-green-200', text: 'text-green-700', label: 'Likely Human-Made' }
  return { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', label: 'Inconclusive' }
}

function ScoreGauge({ score }: { score: number }) {
  const radius = 52
  const strokeWidth = 10
  const circumference = Math.PI * radius
  const filled = circumference * score

  const strokeColor =
    score >= 0.7 ? '#EF4444' : score >= 0.3 ? '#EAB308' : '#22C55E'

  return (
    <div className="flex flex-col items-center">
      <svg width="130" height="75" viewBox="0 0 130 75" className="overflow-visible">
        <path
          d="M 10 65 A 52 52 0 0 1 120 65"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <motion.path
          d="M 10 65 A 52 52 0 0 1 120 65"
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          initial={{ strokeDasharray: `0 ${circumference}` }}
          animate={{ strokeDasharray: `${filled} ${circumference}` }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
        />
      </svg>
      <span className="text-2xl font-bold text-gray-800 -mt-6">
        {Math.round(score * 100)}%
      </span>
      <span className="text-xs text-gray-500 mt-1">avg. AI probability</span>
    </div>
  )
}

function DistributionBar({ summary, total }: {
  summary: { ai: number; human: number; uncertain: number }
  total: number
}) {
  if (total === 0) return null
  const aiPct = (summary.ai / total) * 100
  const humanPct = (summary.human / total) * 100
  const uncertainPct = (summary.uncertain / total) * 100

  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
        {aiPct > 0 && (
          <motion.div
            className="bg-red-500"
            initial={{ width: 0 }}
            animate={{ width: `${aiPct}%` }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.3 }}
          />
        )}
        {uncertainPct > 0 && (
          <motion.div
            className="bg-yellow-400"
            initial={{ width: 0 }}
            animate={{ width: `${uncertainPct}%` }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.4 }}
          />
        )}
        {humanPct > 0 && (
          <motion.div
            className="bg-green-500"
            initial={{ width: 0 }}
            animate={{ width: `${humanPct}%` }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.5 }}
          />
        )}
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
          AI ({summary.ai})
        </span>
        {summary.uncertain > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />
            Uncertain ({summary.uncertain})
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
          Human ({summary.human})
        </span>
      </div>
    </div>
  )
}

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.3, ease: 'easeOut' },
  }),
}

export default function Check() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const isSharedView = !!id

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<CheckResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [checkId, setCheckId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Load shared result
  useEffect(() => {
    if (id) {
      setIsLoading(true)
      fetch(`${API_URL}/check/${id}`)
        .then(res => {
          if (!res.ok) throw new Error('Result not found')
          return res.json()
        })
        .then((data: CheckResponse) => {
          setResult(data)
          setCheckId(data.id || id)
          if (data.imageUrl) setImageUrl(data.imageUrl)
        })
        .catch(() => {
          setError('This check result was not found or has expired.')
        })
        .finally(() => setIsLoading(false))
    }
  }, [id])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      if (imageUrl && !isSharedView) URL.revokeObjectURL(imageUrl)
      setImageUrl(URL.createObjectURL(file))
      setResult(null)
      setError(null)
      setCheckId(null)
      analyzeImage(file)
    }
  }, [imageUrl, isSharedView])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    disabled: isAnalyzing,
  })

  const analyzeImage = async (file: File) => {
    setIsAnalyzing(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('image', file)

      const response = await fetch(`${API_URL}/check`, {
        method: 'POST',
        body: formData,
      })

      const remainingHeader = response.headers.get('X-RateLimit-Remaining')
      if (remainingHeader !== null) {
        setRemaining(parseInt(remainingHeader, 10))
      }

      if (response.status === 429) {
        const data = await response.json()
        setError(data.message || 'Rate limit exceeded. Try again later.')
        return
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Server error' }))
        setError(data.error || `Server error (${response.status})`)
        return
      }

      const data: CheckResponse = await response.json()
      setResult(data)
      if (data.id) setCheckId(data.id)
      if (data.remaining !== undefined) {
        setRemaining(data.remaining)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to server')
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Ctrl+V paste support
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (isAnalyzing) return

      const items = e.clipboardData?.items
      if (!items) return

      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (!file) continue

          if (imageUrl && !isSharedView) URL.revokeObjectURL(imageUrl)
          setImageUrl(URL.createObjectURL(file))
          setResult(null)
          setError(null)
          setCheckId(null)
          analyzeImage(file)
          break
        }
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalyzing, imageUrl, isSharedView])

  const handleReset = () => {
    if (imageUrl && !isSharedView) URL.revokeObjectURL(imageUrl)
    setImageUrl(null)
    setResult(null)
    setError(null)
    setCheckId(null)
    if (isSharedView) navigate('/check')
  }

  const handleShare = async () => {
    if (!checkId) return
    const url = `${window.location.origin}/check/${checkId}`

    // Always copy to clipboard first
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API may fail without HTTPS or focus — fallback
      const textarea = document.createElement('textarea')
      textarea.value = url
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    // On mobile, also open native share dialog
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: `AI Check: ${result ? getVerdictStyle(result.verdict).label : ''}`,
          url,
        })
      } catch {
        // User cancelled share
      }
    }
  }

  const successResults = (result?.results.filter(r => r.label !== null) || [])
    .slice()
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
  const errorResults = result?.results.filter(r => r.error) || []

  const totalSuccessful = result
    ? result.summary.ai + result.summary.human + result.summary.uncertain
    : 0
  const majorityCount = result ? Math.max(result.summary.ai, result.summary.human) : 0
  const agreementRatio = totalSuccessful > 0 ? majorityCount / totalSuccessful : 0
  const confidenceText = agreementRatio >= 0.8 ? 'High' : agreementRatio >= 0.6 ? 'Moderate' : 'Low'
  const avgScore = successResults.length > 0
    ? successResults.reduce((sum, r) => sum + (r.score ?? 0), 0) / successResults.length
    : 0

  const seoTitle = result && isSharedView
    ? `AI Check: ${getVerdictStyle(result.verdict).label}`
    : 'Free AI Detector - Check Images with 11 Detectors Instantly'
  const seoDescription = result && isSharedView
    ? `AI probability: ${Math.round(avgScore * 100)}% \u2014 ${successResults.length} detectors analyzed this image`
    : 'Free AI image detector online. Check if any image is AI-generated using 11 detection services at once. Instant results from Hive, SightEngine, AI or Not & more.'

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={isSharedView ? `/check/${id}` : '/check'}
        ogImage={isSharedView && result?.imageUrl ? result.imageUrl : undefined}
        keywords="free ai detector, ai detector free, ai image detector, check ai image, is image ai generated, ai detection online"
      />

      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 text-balance">
              AI Image Checker
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              {isSharedView
                ? 'Shared check result from AI Detector Arena.'
                : 'Upload an image and check it against 9 AI detection services simultaneously. See what each detector thinks.'}
            </p>
            {!isSharedView && remaining !== null && (
              <p className="mt-2 text-sm text-gray-400">
                {remaining > 0
                  ? `${remaining} free check${remaining === 1 ? '' : 's'} remaining this hour`
                  : 'No free checks remaining this hour'}
              </p>
            )}
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Loading shared result */}
            {isLoading && (
              <div className="card text-center py-12">
                <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
                <p className="mt-6 text-lg font-medium text-gray-700">Loading result...</p>
              </div>
            )}

            {/* Upload / Dropzone (only when not in shared view and no image) */}
            {!isSharedView && !isLoading && !imageUrl ? (
              <div
                {...getRootProps()}
                className={cn(
                  'flex flex-col items-center justify-center gap-4 min-h-[300px] p-8',
                  'border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200',
                  isDragActive
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 bg-white hover:border-primary-400 hover:bg-primary-50/50'
                )}
              >
                <input {...getInputProps()} />
                <div className={cn('p-4 rounded-full', isDragActive ? 'bg-primary-100' : 'bg-gray-100')}>
                  <Upload className={cn('w-8 h-8', isDragActive ? 'text-primary-600' : 'text-gray-400')} />
                </div>
                <div className="text-center">
                  <p className={cn('text-lg font-medium', isDragActive ? 'text-primary-700' : 'text-gray-700')}>
                    {isDragActive ? 'Drop image to analyze' : 'Drop image here to check'}
                  </p>
                  <p className="mt-1 text-gray-500">or click to browse</p>
                </div>
                <p className="text-sm text-gray-400">PNG, JPG, WebP up to 10MB &middot; Ctrl+V to paste</p>
              </div>
            ) : !isLoading && (imageUrl || error || result) ? (
              <div className="space-y-6">
                {/* Analyzing State */}
                {isAnalyzing && (
                  <div className="card text-center py-12">
                    <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
                    <p className="mt-6 text-lg font-medium text-gray-700">Analyzing image with 9 detectors...</p>
                    <p className="mt-2 text-gray-500">This may take up to 30 seconds</p>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="card bg-red-50 border-red-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium text-red-700">
                          {isSharedView ? 'Result not found' : 'Analysis failed'}
                        </p>
                        <p className="mt-1 text-sm text-red-600">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Results */}
                {result && !isAnalyzing && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="space-y-6"
                  >
                    {/* Summary + Image */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Image Preview */}
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Analyzed Image</p>
                        <div className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden">
                          {imageUrl ? (
                            <img src={imageUrl} alt="Analyzed" className="w-full h-full object-contain" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <span className="text-sm">Image not available</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Verdict Card */}
                      <div className={cn('card border-2', getVerdictStyle(result.verdict).bg)}>
                        <h3 className="font-semibold text-gray-800 mb-4">Overall Verdict</h3>

                        <div className="flex items-start gap-4">
                          <ScoreGauge score={avgScore} />
                          <div className="flex-1 min-w-0">
                            <p className={cn('text-2xl font-bold', getVerdictStyle(result.verdict).text)}>
                              {getVerdictStyle(result.verdict).label}
                            </p>
                            {totalSuccessful > 0 && (
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="font-semibold">{confidenceText} confidence</span>
                                {' \u2014 '}
                                {majorityCount}/{totalSuccessful} detectors agree
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="mt-5">
                          <DistributionBar summary={result.summary} total={totalSuccessful} />
                        </div>
                      </div>
                    </div>

                    {/* Detector Results Grid */}
                    <div>
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Detector Results</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {successResults.length} detector{successResults.length !== 1 ? 's' : ''} returned results
                        </p>
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {successResults.map((r, i) => (
                          <motion.div
                            key={r.slug}
                            custom={i}
                            initial="hidden"
                            animate="visible"
                            variants={cardVariants}
                            className="card bg-white"
                          >
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-medium text-gray-800 truncate">{r.name}</span>
                              </div>
                              {r.url && (
                                <a
                                  href={r.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-400 hover:text-primary-500 shrink-0"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                            </div>

                            {/* Label badge */}
                            <div className="mb-2">
                              <span className={cn(
                                'inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border',
                                getLabelColor(r.label)
                              )}>
                                {r.label}
                              </span>
                            </div>

                            {/* Score bar */}
                            {r.score !== null && (
                              <div>
                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                  <span>AI Probability</span>
                                  <span className="font-medium">{(r.score * 100).toFixed(0)}%</span>
                                </div>
                                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <motion.div
                                    className={cn('h-full rounded-full', getScoreBarColor(r.score))}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${Math.max(r.score * 100, 2)}%` }}
                                    transition={{ duration: 0.6, delay: i * 0.06 + 0.2, ease: 'easeOut' }}
                                  />
                                </div>
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </div>

                      {/* Unavailable detectors */}
                      {errorResults.length > 0 && (
                        <details className="mt-4 group">
                          <summary className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer hover:text-gray-600 list-none select-none">
                            <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                            {errorResults.length} detector{errorResults.length !== 1 ? 's' : ''} unavailable
                          </summary>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {errorResults.map(r => (
                              <span key={r.slug} className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded-full">
                                {r.name}
                              </span>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Actions */}
                {(result || error) && !isAnalyzing && (
                  <div className="flex items-center justify-center gap-3">
                    <Button variant="secondary" onClick={handleReset} size="lg">
                      <RefreshCw className="w-5 h-5" />
                      {isSharedView ? 'Check Your Own Image' : 'Check Another Image'}
                    </Button>
                    {checkId && result && (
                      <Button variant="primary" onClick={handleShare} size="lg">
                        {copied ? (
                          <>
                            <CheckIcon className="w-5 h-5" />
                            Link Copied!
                          </>
                        ) : (
                          <>
                            <Share2 className="w-5 h-5" />
                            Share Results
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </>
  )
}
